import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { getThemeAssets } from '@/utils/theme';
import { isErpHost, erpPath } from '@/utils/erp-host';

// Dedicated School ERP staff login (phone + WhatsApp OTP).
// Authenticates pa_users staff directly and stores a pa-token in `erp_auth_token`,
// which apiRequest sends on /parent-app/* calls. Distinct from the CRM login.

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5004/api';

const ErpLogin: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [otp, setOTP] = useState(['', '', '', '', '', '']);
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const redirectTo = searchParams.get('redirect') || erpPath('dashboard');
  const theme = getThemeAssets();

  useEffect(() => {
    if (isOTPSent) otpInputRefs.current[0]?.focus();
  }, [isOTPSent]);

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(phone)) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await fetch(`${API_BASE_URL}/parent-app/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send OTP');
      setIsOTPSent(true);
      setSuccessMessage('OTP sent! Please check your WhatsApp.');
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPChange = (index: number, value: string) => {
    if (value && !/^\d+$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOTP(next);
    if (value && index < 5) otpInputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) otpInputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const next = [...otp];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setOTP(next);
    otpInputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const doLogin = (preferredRole?: string) =>
        fetch(`${API_BASE_URL}/parent-app/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone,
            otp: otpCode,
            device_type: 'web',
            ...(preferredRole ? { preferred_role: preferredRole } : {}),
          }),
        });

      let res = await doLogin();
      let data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Invalid OTP');

      // Dual-role accounts (a teacher who is also a parent contact) get asked
      // to pick a role before the OTP is consumed (backend peeks, doesn't
      // consume, on the first call specifically so it can be reused here).
      // The web ERP is staff-only, so resolve this silently as 'teacher'
      // instead of blocking a legitimate staff member from logging in.
      if (data.data?.requires_role_selection) {
        res = await doLogin('teacher');
        data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Invalid OTP');
      }

      const accessToken = data.data?.tokens?.accessToken;
      if (!accessToken) throw new Error('Login failed — no token returned');

      // pa-tokens are long-lived; scope the web cookie to a session-ish window.
      Cookies.set('erp_auth_token', accessToken, { expires: 1, sameSite: 'Lax' });
      // Dedicated identity source for the ERP UI (sidebar badge, nav permission
      // filtering) — distinct from the CRM login's `crm_user` key, which this
      // phone+OTP flow never populates.
      if (data.data?.user) {
        localStorage.setItem('erp_user', JSON.stringify(data.data.user));
      }
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      setError(err.message || 'OTP verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOTP(['', '', '', '', '', '']);
    setError(null);
    await handleSendOTP(new Event('submit') as unknown as React.FormEvent);
  };

  return (
    <div className="min-h-screen w-screen flex flex-col-reverse lg:flex-row m-0 p-0">
      {/* Left — brand panel */}
      <div className="w-full h-[40vh] lg:w-1/2 lg:h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="relative z-10 h-full flex flex-col justify-center items-center px-12 py-16 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-3 text-white leading-tight">
            Welcome back 👋
          </h1>
          <p className="text-xl text-yellow-400 font-semibold mb-2">School ERP · Operations Center</p>
          <p className="text-sm text-slate-300 max-w-sm">
            {!isOTPSent
              ? 'Sign in with your registered mobile number to manage classes, attendance, fees and more.'
              : 'Enter the 6-digit code sent to your WhatsApp.'}
          </p>
        </div>
      </div>

      {/* Right — form */}
      <div className="w-full lg:w-1/2 lg:h-screen bg-white flex items-center justify-center px-4 py-8 sm:px-6">
        <div className="w-full max-w-md mx-auto">
          <div className="text-center mb-6">
            <img
              src={theme.loginLogo}
              alt={theme.brandName}
              className="h-10 sm:h-12 mx-auto mb-4"
            />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
              {!isOTPSent ? 'Staff Sign In' : 'Verify OTP'}
            </h1>
            <p className="text-gray-500 text-sm">
              {!isOTPSent ? "We'll send a verification code to your WhatsApp" : 'Check your WhatsApp for the code'}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 text-sm">{successMessage}</p>
            </div>
          )}

          {!isOTPSent && (
            <form onSubmit={handleSendOTP} className="space-y-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2 text-left">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter your mobile number"
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                  disabled={isLoading}
                  maxLength={10}
                />
              </div>
              <button
                type="submit"
                disabled={isLoading || !phone}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white py-3 px-4 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
              >
                {isLoading ? 'Sending OTP…' : 'Send OTP via WhatsApp'}
              </button>
            </form>
          )}

          {isOTPSent && (
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <div>
                <p className="text-sm text-gray-500 text-center mb-4">
                  OTP sent to <span className="font-semibold text-gray-800">+91 {phone}</span>
                </p>
                <div className="flex justify-center gap-1.5 sm:gap-2" onPaste={handlePaste}>
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => { otpInputRefs.current[index] = el; }}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOTPChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="w-10 h-12 sm:w-12 sm:h-12 text-center text-lg sm:text-xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                      disabled={isLoading}
                    />
                  ))}
                </div>
              </div>
              <button
                type="submit"
                disabled={isLoading || otp.join('').length !== 6}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-3 px-4 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
              >
                {isLoading ? 'Verifying…' : 'Verify & Sign In'}
              </button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={isLoading}
                  className="text-yellow-600 hover:text-yellow-700 text-sm font-medium transition-colors"
                >
                  Didn't receive OTP? Resend
                </button>
              </div>
            </form>
          )}

          {!isErpHost() && (
            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/login')}
                className="text-gray-500 hover:text-gray-700 text-sm transition-colors"
              >
                ← CRM Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErpLogin;
